/**
 * ExerciseProgress - Shows detailed progress tracking for a specific exercise
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Barbell,
  CalendarBlank,
  TrendDown,
  TrendUp,
  type Icon,
} from '@phosphor-icons/react';
import {
  Timestamp,
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { useAuth } from '@/core/AuthContext';
import { db } from '@/core/firebase';
import { SectionCard } from '../../../components/trainer/SectionCard';
import { ProgressChart, type DataPoint } from './ProgressChart';
import type { SetLogRecord, WorkoutSession } from '../../../types';

interface ExerciseSummary {
  exerciseId: string;
  exerciseName: string;
  sessionCount: number;
  totalSets: number;
  maxWeight: number;
  lastPerformed: Date;
  trend: number;
  recentWeights: number[];
}

export function ExerciseProgress() {
  const { user, loading } = useAuth();
  const [setLogs, setSetLogs] = useState<SetLogRecord[]>([]);
  const [sessions, setSessions] = useState<WorkoutSession[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const selectedExerciseId = searchParams.get('exercise');

  useEffect(() => {
    if (loading || !user) return;

    const logsQuery = query(
      collection(db, 'setLogs'),
      where('clientId', '==', user.uid),
      orderBy('performedAt', 'desc')
    );

    const unsubLogs = onSnapshot(logsQuery, (snapshot) => {
      const docs = snapshot.docs.map(
        (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as SetLogRecord)
      );
      setSetLogs(docs);
    });

    // Also fetch sessions for exercise name lookup
    const sessionsQuery = query(
      collection(db, 'workoutSessions'),
      where('clientId', '==', user.uid)
    );

    const unsubSessions = onSnapshot(sessionsQuery, (snapshot) => {
      const docs = snapshot.docs.map(
        (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as WorkoutSession)
      );
      setSessions(docs);
    });

    return () => {
      unsubLogs();
      unsubSessions();
    };
  }, [user, loading]);

  // Build exercise name lookup from sessions
  const exerciseNameMap = useMemo(() => {
    const nameMap = new Map<string, string>();
    sessions.forEach((session) => {
      session.workoutSnapshot?.items?.forEach((item) => {
        if (item.exerciseId && item.exerciseName && !nameMap.has(item.exerciseId)) {
          nameMap.set(item.exerciseId, item.exerciseName);
        }
      });
    });
    return nameMap;
  }, [sessions]);

  // Group logs by exercise and calculate summaries
  const exerciseSummaries = useMemo(() => {
    const exerciseMap = new Map<string, SetLogRecord[]>();
    
    setLogs.forEach((log) => {
      const key = log.exerciseId;
      if (!exerciseMap.has(key)) {
        exerciseMap.set(key, []);
      }
      exerciseMap.get(key)!.push(log);
    });

    const summaries: ExerciseSummary[] = [];
    
    exerciseMap.forEach((logs, exerciseId) => {
      // Get exercise name from session item (we don't have direct exercise reference)
      // Use the most recent log to get the name
      const sortedLogs = [...logs].sort((a, b) => {
        const dateA = (a.performedAt as Timestamp)?.toDate?.() || new Date(0);
        const dateB = (b.performedAt as Timestamp)?.toDate?.() || new Date(0);
        return dateB.getTime() - dateA.getTime();
      });

      // Get unique sessions (by date)
      const sessionDates = new Set<string>();
      const weightsByDate = new Map<string, number>();
      
      sortedLogs.forEach((log) => {
        const date = (log.performedAt as Timestamp)?.toDate?.() || new Date();
        const dateKey = date.toDateString();
        sessionDates.add(dateKey);
        
        // Track max weight per session
        const currentMax = weightsByDate.get(dateKey) || 0;
        if (log.weight && log.weight > currentMax) {
          weightsByDate.set(dateKey, log.weight);
        }
      });

      const weights = Array.from(weightsByDate.values()).reverse(); // oldest to newest
      const maxWeight = Math.max(...logs.map((l) => l.weight || 0), 0);
      const lastPerformed = (sortedLogs[0]?.performedAt as Timestamp)?.toDate?.() || new Date();
      
      // Calculate trend (difference between first and last session max)
      const trend = weights.length > 1 
        ? weights[weights.length - 1] - weights[0]
        : 0;

      // Get exercise name from sessions, or format from ID as fallback
      const exerciseName = exerciseNameMap.get(exerciseId) || formatExerciseId(exerciseId);

      summaries.push({
        exerciseId,
        exerciseName,
        sessionCount: sessionDates.size,
        totalSets: logs.length,
        maxWeight,
        lastPerformed,
        trend,
        recentWeights: weights.slice(-5),
      });
    });

    // Sort by most recently performed
    return summaries.sort((a, b) => b.lastPerformed.getTime() - a.lastPerformed.getTime());
  }, [setLogs, exerciseNameMap]);

  // Get selected exercise data
  const selectedExercise = useMemo(() => {
    if (!selectedExerciseId) return null;
    return exerciseSummaries.find((e) => e.exerciseId === selectedExerciseId);
  }, [selectedExerciseId, exerciseSummaries]);

  // Build chart data for selected exercise
  const chartData = useMemo(() => {
    if (!selectedExerciseId) return null;

    const exerciseLogs = setLogs.filter((log) => log.exerciseId === selectedExerciseId);
    if (exerciseLogs.length === 0) return null;

    // Get max weight and total reps per session date
    const sessionData = new Map<string, { date: Date; maxWeight: number; totalReps: number; totalVolume: number }>();

    exerciseLogs.forEach((log) => {
      const date = (log.performedAt as Timestamp)?.toDate?.() || new Date();
      const dateKey = date.toDateString();
      
      const existing = sessionData.get(dateKey) || { 
        date, 
        maxWeight: 0, 
        totalReps: 0,
        totalVolume: 0 
      };
      
      if (log.weight && log.weight > existing.maxWeight) {
        existing.maxWeight = log.weight;
      }
      existing.totalReps += log.reps || 0;
      existing.totalVolume += (log.weight || 0) * (log.reps || 0);
      
      sessionData.set(dateKey, existing);
    });

    const sortedSessions = Array.from(sessionData.values())
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    const weightPoints: DataPoint[] = sortedSessions.map((s) => ({
      date: s.date,
      value: s.maxWeight,
    }));

    const repPoints: DataPoint[] = sortedSessions.map((s) => ({
      date: s.date,
      value: s.totalReps,
    }));

    const volumePoints: DataPoint[] = sortedSessions.map((s) => ({
      date: s.date,
      value: s.totalVolume,
    }));

    return { weightPoints, repPoints, volumePoints };
  }, [selectedExerciseId, setLogs]);

  // Get set history for selected exercise
  const setHistory = useMemo(() => {
    if (!selectedExerciseId) return [];
    
    return setLogs
      .filter((log) => log.exerciseId === selectedExerciseId)
      .slice(0, 20) // Last 20 sets
      .map((log) => ({
        date: (log.performedAt as Timestamp)?.toDate?.() || new Date(),
        weight: log.weight || 0,
        reps: log.reps || 0,
        setIndex: log.setIndex,
      }));
  }, [selectedExerciseId, setLogs]);

  const handleSelectExercise = (exerciseId: string) => {
    setSearchParams({ exercise: exerciseId });
  };

  const handleBack = () => {
    if (selectedExerciseId) {
      setSearchParams({});
    } else {
      navigate('/client/progress');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-gray-600 dark:text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <button
            onClick={handleBack}
            className="mt-1 p-2 rounded-lg text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-white/10 transition"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <p className="text-sm text-primary-600 dark:text-primary-400 uppercase tracking-wide font-semibold">
              Exercise Tracking
            </p>
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
              {selectedExercise ? selectedExercise.exerciseName : 'Exercise Progress'}
            </h1>
            <p className="text-sm text-gray-600 dark:text-slate-400">
              {selectedExercise 
                ? `${selectedExercise.sessionCount} sessions tracked`
                : 'Track your strength gains for individual exercises'
              }
            </p>
          </div>
        </div>

        {/* Exercise List or Detail View */}
        {!selectedExerciseId ? (
          // Exercise List
          <SectionCard
            eyebrow="Exercises"
            title="Your Tracked Exercises"
            action={
              <span className="text-xs text-gray-600 dark:text-slate-400">
                {exerciseSummaries.length} exercises
              </span>
            }
          >
            {exerciseSummaries.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600 dark:text-slate-400">
                  No exercise data yet. Complete some workouts to see your progress!
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {exerciseSummaries.map((exercise) => (
                  <button
                    key={exercise.exerciseId}
                    onClick={() => handleSelectExercise(exercise.exerciseId)}
                    className="w-full text-left p-4 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 transition"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {exercise.exerciseName}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-slate-400">
                          {exercise.sessionCount} sessions · Max: {exercise.maxWeight} lb
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                          Last: {exercise.lastPerformed.toLocaleDateString()}
                        </p>
                      </div>
                      
                      {/* Mini sparkline */}
                      {exercise.recentWeights.length > 1 && (
                        <div className="flex items-center gap-2">
                          <MiniSparkline 
                            data={exercise.recentWeights} 
                            trend={exercise.trend}
                          />
                          <span className={`text-sm font-medium ${
                            exercise.trend >= 0 
                              ? 'text-green-600 dark:text-green-400' 
                              : 'text-red-600 dark:text-red-400'
                          }`}>
                            {exercise.trend >= 0 ? '+' : ''}{exercise.trend}
                          </span>
                        </div>
                      )}
                      
                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </SectionCard>
        ) : (
          // Exercise Detail View
          <>
            {/* Weight Progress Chart */}
            {chartData && chartData.weightPoints.length > 0 && (
              <SectionCard
                eyebrow="Progress"
                title="Weight Progression"
              >
                <ProgressChart
                  data={chartData.weightPoints}
                  height={200}
                  title="Max Weight"
                  unit="lb"
                  formatValue={(v) => Math.round(v).toString()}
                  color={selectedExercise?.trend && selectedExercise.trend >= 0 ? '#10b981' : '#ef4444'}
                />
              </SectionCard>
            )}

            {/* Reps Progress Chart */}
            {chartData && chartData.repPoints.length > 0 && (
              <SectionCard
                eyebrow="Reps"
                title="Total Reps Per Session"
              >
                <ProgressChart
                  data={chartData.repPoints}
                  height={180}
                  title="Reps"
                  unit="reps"
                  formatValue={(v) => Math.round(v).toString()}
                  color="#6366f1"
                />
              </SectionCard>
            )}

            {/* Stats Grid */}
            {selectedExercise && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <StatCard
                  label="Max Weight"
                  value={`${selectedExercise.maxWeight}`}
                  unit="lb"
                  icon={Barbell}
                />
                <StatCard
                  label="Sessions"
                  value={`${selectedExercise.sessionCount}`}
                  icon={CalendarBlank}
                />
                <StatCard
                  label="Total Sets"
                  value={`${selectedExercise.totalSets}`}
                  icon={Barbell}
                />
                <StatCard
                  label="Progress"
                  value={`${selectedExercise.trend >= 0 ? '+' : ''}${selectedExercise.trend}`}
                  unit="lb"
                  icon={selectedExercise.trend >= 0 ? TrendUp : TrendDown}
                  highlight={selectedExercise.trend >= 0 ? 'positive' : 'negative'}
                />
              </div>
            )}

            {/* Recent Sets */}
            <SectionCard
              eyebrow="History"
              title="Recent Sets"
            >
              {setHistory.length === 0 ? (
                <p className="text-gray-600 dark:text-slate-400 text-sm">No sets recorded yet.</p>
              ) : (
                <div className="space-y-2">
                  {setHistory.map((set, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-white/5"
                    >
                      <div>
                        <p className="text-sm text-gray-600 dark:text-slate-400">
                          {set.date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-500">
                          Set {set.setIndex + 1}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {set.weight} lb × {set.reps}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-500">
                          Volume: {set.weight * set.reps} lb
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </>
        )}
      </div>
    </div>
  );
}

// Helper component for mini sparkline
function MiniSparkline({ data, trend }: { data: number[]; trend: number }) {
  if (data.length < 2) return null;
  
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  
  const width = 60;
  const height = 20;
  
  const points = data.map((value, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((value - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} className="inline-block">
      <polyline
        points={points}
        fill="none"
        stroke={trend >= 0 ? '#10b981' : '#ef4444'}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Helper component for stat cards
function StatCard({ 
  label, 
  value, 
  unit, 
  icon,
  highlight 
}: { 
  label: string; 
  value: string; 
  unit?: string; 
  icon: Icon;
  highlight?: 'positive' | 'negative';
}) {
  const Icon = icon;
  return (
    <div className="p-4 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-gray-700 dark:text-slate-300">
          <Icon size={18} weight="bold" />
        </span>
        <span className="text-xs text-gray-500 dark:text-slate-500 uppercase tracking-wide">
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className={`text-2xl font-bold ${
          highlight === 'positive' 
            ? 'text-green-600 dark:text-green-400'
            : highlight === 'negative'
            ? 'text-red-600 dark:text-red-400'
            : 'text-gray-900 dark:text-white'
        }`}>
          {value}
        </span>
        {unit && (
          <span className="text-sm text-gray-500 dark:text-slate-500">{unit}</span>
        )}
      </div>
    </div>
  );
}

// Helper to format exercise ID as a readable name
function formatExerciseId(exerciseId: string): string {
  return exerciseId
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\s+/, '')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export default ExerciseProgress;
